import { DAILY_SCORE_ATTACK, FREEDOM, HUNT, LADDER } from '../engine/modes.ts';
import { LADDER_RUNGS } from '../engine/board.ts';
import { ACHIEVEMENTS_PER_DAY } from '../engine/achievements.ts';

// ***************************************************************************************
// The words each mode uses about itself.
//
// One table rather than a chain of "is it ladder? is it hunt?" tucked inside the screens.
// Four modes is the point where those chains stop being readable, and every one of them
// has to be found and extended when a fifth arrives - which is exactly how a new mode
// ends up quietly describing itself as the old one.
//
// Three rules on the door, never more. Everything else - diagonals, rarity scoring, no
// timer - is discoverable in the first thirty seconds of play, and a wall of rules is
// what people skip past.
// ***************************************************************************************

export interface ModeCopy {
  /** The entry screen's headline. */
  readonly title: string;
  /** What a puzzle of this mode is called, next to its number. */
  readonly label: string;
  readonly rules: readonly string[];
  readonly play: string;
  /** What the board says when nothing is selected. */
  readonly selectHint: string;
}

const COPY: Readonly<Record<string, ModeCopy>> = {
  [DAILY_SCORE_ATTACK.id]: {
    title: 'Your daily word puzzle.',
    label: 'Puzzle',
    rules: [
      'Join touching letters to make words.',
      'Used tiles vanish. The rest fall and pack right.',
      'Highest score wins. No timer.',
    ],
    play: 'Play today’s puzzle',
    selectHint: 'Drag or tap letters to make a word',
  },

  [LADDER.id]: {
    title: 'Climb the ladder.',
    label: 'Ladder',
    rules: [
      `Find one word of each length: ${LADDER_RUNGS.join(', ')}. In any order.`,
      'Every board has all five. They use up the grid exactly.',
      'Used tiles vanish. The rest fall and pack right.',
    ],
    play: 'Play today’s ladder',
    selectHint: 'Drag or tap letters to make a word',
  },

  [HUNT.id]: {
    title: 'Hunt the day’s five.',
    label: 'Hunt',
    rules: [
      `${ACHIEVEMENTS_PER_DAY} challenges a day. Take one and that word scores double.`,
      'Every challenge has an answer on the board. Finding it is your problem.',
      'Three shuffles if you get stuck.',
    ],
    play: 'Play today’s hunt',
    selectHint: 'Drag or tap letters to make a word',
  },

  [FREEDOM.id]: {
    title: 'Any letters. Anywhere.',
    label: 'Freedom',
    rules: [
      'Take letters from anywhere on the grid. They do not have to touch.',
      'Used tiles vanish. The rest fall and pack right.',
      'Highest score wins. No timer.',
    ],
    play: 'Play today’s grid',
    selectHint: 'Tap letters anywhere to make a word',
  },
};

export function copyFor(modeId: string): ModeCopy {
  return COPY[modeId] ?? COPY[DAILY_SCORE_ATTACK.id]!;
}
